import React, { useEffect, useRef, useState } from 'react';
import { Rnd } from 'react-rnd';
import styled from 'styled-components';
import {
  BASE_WIDGET_CLASS,
  DASHBOARD_PAGE_ID,
  INITIAL_POSITION_X,
  INITIAL_POSITION_Y,
  MIN_CSS_HEIGHT,
  MIN_CSS_WIDTH,
} from '../../constants/constants';
import { debounce } from 'lodash';

export interface HtmlElementSize {
  width: string;
  height: string;
}

export interface HtmlElementPosition {
  x: number;
  y: number;
}

export interface BaseWidgetProps {
  header: React.ReactNode | string;
  onPositionChange: (newPosition: HtmlElementPosition) => void;
  onSizeChange: (newSize: HtmlElementSize) => void;
  initialSize?: HtmlElementSize;
  initialPosition?: HtmlElementPosition;
  children: React.ReactNode;
}

export const StyledWidgetContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.BORDER};
  box-shadow: 0 1px 1px rgb(0 0 0 / 4%);
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.CARD};
`;

export const StyledBaseWidgetHeader = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.BORDER};
`;

export const StyledWidgetBody = styled.div`
  flex-grow: 1;
  overflow: auto;
`;

const BaseWidgetComponent = ({
  header,
  onPositionChange,
  onSizeChange,
  initialSize,
  initialPosition = { x: INITIAL_POSITION_X, y: INITIAL_POSITION_Y },
  children,
}: BaseWidgetProps) => {
  const [position, setPosition] = useState<HtmlElementPosition>(initialPosition);

  const [resizeableSize, setResizeableSize] = useState<HtmlElementSize>({
    width: initialSize ? initialSize.width : MIN_CSS_WIDTH,
    height: initialSize ? initialSize.height : MIN_CSS_HEIGHT,
  });

  const lastManualWidth = useRef<string | null>(null);

  useEffect(() => {
    const parentContainer = document.querySelector(`#${DASHBOARD_PAGE_ID}`);
    const rndElement = document.querySelector(`.${BASE_WIDGET_CLASS}`);

    if (!parentContainer) return;

    const updateWidth = debounce(() => {
      const parentWidth = parentContainer.clientWidth;
      const rndWidth = rndElement!.clientWidth;
      const rndHeight = rndElement!.clientHeight;
      const rndPosX = rndElement!.getBoundingClientRect().x - parentContainer.getBoundingClientRect().x;

      if (rndWidth + rndPosX > parentWidth) {
        lastManualWidth.current = `${rndWidth}px`;

        const newSize = {
          width: `${parentWidth - rndPosX}px`,
          height: `${rndHeight}px` ,
        };

        setResizeableSize(newSize);
        onSizeChange(newSize);
      } else if (lastManualWidth.current) {
        const newSize = {
          width: lastManualWidth.current!,
          height: `${rndHeight}px`,
        };

        setResizeableSize(newSize);
        onSizeChange(newSize);
        lastManualWidth.current = null;
      }
    }, 100);

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(parentContainer);

    return () => {
      resizeObserver.unobserve(parentContainer);
    };
  }, []);

  return (
    <Rnd
      position={position}
      size={resizeableSize}
      minWidth={MIN_CSS_WIDTH}
      minHeight={MIN_CSS_HEIGHT}
      dragHandleClassName="drag-handler"
      className={BASE_WIDGET_CLASS}
      onDragStop={(e, d) => {
        const newPosition = { x: d.x, y: d.y };
        setPosition(newPosition);
        onPositionChange(newPosition);
      }}
      onResizeStop={(e, direction, ref, delta, position) => {
        const newSize = { width: ref.style.width, height: ref.style.height };
        setPosition({ ...position });
        setResizeableSize(newSize);
        onPositionChange(position);
        onSizeChange(newSize);
      }}
      bounds={`#${DASHBOARD_PAGE_ID}`}
    >
      <StyledWidgetContainer>
        <StyledBaseWidgetHeader className="drag-handler">
          {header}
        </StyledBaseWidgetHeader>
        <StyledWidgetBody>{children}</StyledWidgetBody>
      </StyledWidgetContainer>
    </Rnd>
  );
};

export default BaseWidgetComponent;
